/**
 * Device Detection Utilities
 * Provides comprehensive device, platform, and capability detection
 */

/**
 * Detect if the current device is a mobile device (phone or tablet)
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Detect if the current device is specifically a phone (not tablet)
 */
export function isPhone(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;

  // Check for phone-specific indicators
  const isSmallScreen = window.innerWidth < 768;
  const isMobile = isMobileDevice();

  return isMobile && isSmallScreen;
}

/**
 * Detect if the current device is a tablet
 */
export function isTablet(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;

  const isMobile = isMobileDevice();
  const isLargeScreen = window.innerWidth >= 768 && window.innerWidth < 1024;

  // iPad specifically
  const isIPad = /iPad|Macintosh/i.test(navigator.userAgent) && 'ontouchend' in document;

  return (isMobile && isLargeScreen) || isIPad;
}

/**
 * Detect if running on iOS (iPhone, iPad, iPod)
 */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;

  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
         // iPad on iOS 13+ detection
         (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
}

/**
 * Detect if running on Android
 */
export function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;

  return /Android/i.test(navigator.userAgent);
}

/**
 * Detect if running on macOS
 */
export function isMacOS(): boolean {
  if (typeof navigator === 'undefined') return false;

  return /Mac|Macintosh|MacIntel|MacPPC|Mac68K/i.test(navigator.userAgent) &&
         !('ontouchend' in document); // Exclude iPads
}

/**
 * Detect if running on Windows
 */
export function isWindows(): boolean {
  if (typeof navigator === 'undefined') return false;

  return /Win|Windows/i.test(navigator.userAgent);
}

/**
 * Detect if running on Linux
 */
export function isLinux(): boolean {
  if (typeof navigator === 'undefined') return false;

  return /Linux/i.test(navigator.userAgent) && !isAndroid();
}

/**
 * Detect if device has touch capability
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;

  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0
  );
}

/**
 * Detect if device uses coarse pointer (touch) vs fine pointer (mouse)
 * This is the most reliable way to detect touch-only devices
 */
export function hasCoarsePointer(): boolean {
  if (typeof window === 'undefined') return false;

  return window.matchMedia('(pointer: coarse)').matches;
}

/**
 * Detect if device can hover (has mouse)
 */
export function canHover(): boolean {
  if (typeof window === 'undefined') return false;

  return window.matchMedia('(hover: hover)').matches;
}

/**
 * Detect if Web Speech API is available
 */
export function supportsWebSpeechAPI(): boolean {
  if (typeof window === 'undefined') return false;

  return 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;
}

/**
 * Detect if Visual Viewport API is available
 */
export function supportsVisualViewport(): boolean {
  if (typeof window === 'undefined') return false;

  return 'visualViewport' in window;
}

/**
 * Determine if we should use native Web Speech API instead of Whisper
 * Based on: mobile devices or macOS
 */
export function shouldUseNativeSpeech(): boolean {
  const hasAPI = supportsWebSpeechAPI();
  const isMobileOrMac = isMobileDevice() || isMacOS();

  return hasAPI && isMobileOrMac;
}

/**
 * Get the platform name
 */
export function getPlatform(): 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'unknown' {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  if (isMacOS()) return 'macos';
  if (isWindows()) return 'windows';
  if (isLinux()) return 'linux';
  return 'unknown';
}

/**
 * Get the device type
 */
export function getDeviceType(): 'phone' | 'tablet' | 'desktop' {
  if (isPhone()) return 'phone';
  if (isTablet()) return 'tablet';
  return 'desktop';
}

/**
 * Get comprehensive device information
 */
export interface DeviceInfo {
  platform: 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'unknown';
  deviceType: 'phone' | 'tablet' | 'desktop';
  isMobile: boolean;
  isTouch: boolean;
  canHover: boolean;
  supportsWebSpeech: boolean;
  supportsVisualViewport: boolean;
  shouldUseNativeSpeech: boolean;
}

export function getDeviceInfo(): DeviceInfo {
  return {
    platform: getPlatform(),
    deviceType: getDeviceType(),
    isMobile: isMobileDevice(),
    isTouch: hasCoarsePointer() || isTouchDevice(),
    canHover: canHover(),
    supportsWebSpeech: supportsWebSpeechAPI(),
    supportsVisualViewport: supportsVisualViewport(),
    shouldUseNativeSpeech: shouldUseNativeSpeech(),
  };
}
